import { useCallback } from "react";
import { getTechCommission } from "../../../utils/job-form-utils";
import { addHoursToTime } from "../../../utils/dateUtils";

/**
 * Custom hook for job source, technician, and service selection handlers.
 * Manages commission auto-population when selections change.
 */
export function useJobSelections(setFormData, jobSources, jobTypes, teamMembers) {

  const handleSourceSelect = useCallback((sourceId) => {
    setFormData(prev => {
      const newData = { ...prev, source_id: sourceId };
      if (sourceId && sourceId.trim()) {
        const source = jobSources.find(s => s.source_id === sourceId);
        if (source && source.commission_value > 0) {
          newData.source_commission_type = source.commission_type || "percentage";
          newData.source_commission_value = source.commission_value;
          newData.source_parts_deduction = source.parts_deduction || "from_total";
        }
        if (source) {
          newData.source_extra_fee_enabled = !!source.extra_fee_enabled;
          newData.source_extra_fee_per_job = source.extra_fee_per_job || 0;
        }
      } else {
        newData.source_commission_type = "percentage";
        newData.source_commission_value = "";
        newData.source_parts_deduction = "from_total";
        newData.source_extra_fee_enabled = false;
        newData.source_extra_fee_per_job = 0;
      }
      if (prev.assigned_to && prev.assigned_to.trim()) {
        const tech = teamMembers.find(m => m.member_id === prev.assigned_to);
        const comm = getTechCommission(tech, sourceId, prev.service_id);
        newData.technician_commission_type = comm.type;
        newData.technician_commission_value = comm.value;
      }
      return newData;
    });
  }, [setFormData, jobSources, teamMembers]);

  const handleTechnicianSelect = useCallback((techId) => {
    setFormData(prev => {
      const newData = { ...prev, assigned_to: techId };
      if (techId && techId.trim()) {
        const tech = teamMembers.find(m => m.member_id === techId);
        const comm = getTechCommission(tech, prev.source_id, prev.service_id);
        newData.technician_commission_type = comm.type;
        newData.technician_commission_value = comm.value;
      } else {
        newData.technician_commission_type = "percentage";
        newData.technician_commission_value = "";
        newData.additional_technician_id = "";
        newData.additional_tech_commission_type = "percentage";
        newData.additional_tech_commission_value = "50";
      }
      return newData;
    });
  }, [setFormData, teamMembers]);

  const handleServiceSelect = useCallback((serviceId) => {
    const service = jobTypes.find(jt => jt.service_id === serviceId);
    setFormData(prev => {
      const newData = { ...prev, service_id: serviceId, service_type: service?.type || "other", duration_hours: service?.default_duration || 1 };
      // Service duration changed → recompute the Between-Hours end time
      if (newData.schedule_type === "between" && newData.scheduled_time) {
        newData.scheduled_time_end = addHoursToTime(newData.scheduled_time, newData.duration_hours) || newData.scheduled_time_end;
      }
      if (prev.assigned_to && prev.assigned_to.trim() && serviceId && serviceId.trim()) {
        const tech = teamMembers.find(m => m.member_id === prev.assigned_to);
        if (tech?.services?.length > 0 && !tech.services.includes(serviceId)) {
          newData.assigned_to = "";
          newData.technician_commission_type = "percentage";
          newData.technician_commission_value = "";
          newData.additional_technician_id = "";
          newData.additional_tech_commission_type = "percentage";
          newData.additional_tech_commission_value = "50";
        } else if (tech) {
          const comm = getTechCommission(tech, prev.source_id, serviceId);
          newData.technician_commission_type = comm.type;
          newData.technician_commission_value = comm.value;
        }
      }
      return newData;
    });
  }, [setFormData, jobTypes, teamMembers]);

  return { handleSourceSelect, handleTechnicianSelect, handleServiceSelect };
}
